/*
  FILE NAME: jee2020_1_1.jsx
  USE THIS AS YOUR TEMPLATE FOR ALL OTHER TESTS
*/
import React, { useState, useEffect, useRef } from "react";
import "./testseries.css";
import { storage, db } from "../../firebase";
import { ref as storageRef, listAll, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { doc, runTransaction } from "firebase/firestore";
import { PROGRAM_SUBJECT_KEYS, SUBJECT_CHAPTERS } from "../../config/tutorialCatalogue";

export default function MockTest() {
  const navigate = useNavigate();
  const fullscreenActive = useRef(false);
  const submissionPending = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const [timeLeft, setTimeLeft] = useState(3 * 60 * 60);
  const [currentQues, setCurrentQues] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState([]);
  const [visited, setVisited] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showFSPrompt, setShowFSPrompt] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  const { currentUser, signInWithGoogle } = useAuth();
  const userName = currentUser?.displayName || currentUser?.email;

  // --- 1. CHANGE THIS FOR EACH NEW TEST ---
  const testName = "JEE-Main 2020: 2 Sep 2020 (Shift1) - English";
  const durationInMinutes = 180;
  const numberQuestions = 75;

  // --- 2. CHANGE THIS FOR EACH NEW TEST ---
  const answerKey = {
    1: "4", 2: "3", 3: "2", 4: "1", 5: "4", 6: "2", 7: "3", 8: "1", 9: "1", 10: "1",
    11: "2", 12: "1", 13: "2", 14: "4", 15: "2", 16: "4", 17: "2", 18: "2", 19: "2", 20: "2",
    21: "34", 22: "2035", 23: "16", 24: "34", 25: "216", 26: "3", 27: "2", 28: "4", 29: "1", 30: "4",
    31: "2", 32: "4", 33: "4", 34: "4", 35: "1", 36: "4", 37: "3", 38: "4", 39: "3", 40: "3",
    41: "2", 42: "4", 43: "2", 44: "1", 45: "4", 46: "8", 47: "4", 48: "90", 49: "40", 50: "5",
    51: "2", 52: "2", 53: "3", 54: "4", 55: "3", 56: "3", 57: "2", 58: "4", 59: "2", 60: "2",
    61: "1", 62: "1", 63: "3", 64: "3", 65: "1", 66: "2", 67: "3", 68: "4", 69: "4", 70: "3",
    71: "6", 72: "69", 73: "154", 74: "45", 75: "20"
  };

  // --- 3. CHANGE THIS FOR EACH NEW TEST ---
  const STORAGE_FOLDER = "TestSeries/jee2020_1_1";  //firebase storage folder path (Ques images)

  // --- Responsive ---
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide navbar during test
  useEffect(() => {
    if (showTest || showResult) {
      document.body.classList.add('test-fullscreen-mode');
    } else {
      document.body.classList.remove('test-fullscreen-mode');
    }
    return () => document.body.classList.remove('test-fullscreen-mode');
  }, [showTest, showResult]);

  // --- Track visited questions ---
  useEffect(() => {
    if (questions.length > 0 && questions[currentQues]) {
      const id = questions[currentQues].id;
      setVisited(prev => prev.includes(id) ? prev : [...prev, id]);
    }
  }, [currentQues, questions]);

  // --- Clear spinner for non-image questions ---
  useEffect(() => {
    if (!questionLoading) return;
    const q = questions[currentQues];
    if (q && !q.img) {
      const id = requestAnimationFrame(() => setQuestionLoading(false));
      return () => cancelAnimationFrame(id);
    }
  }, [currentQues, questionLoading, questions]);

  useEffect(() => {
    // Set duration from variable
    setTimeLeft(durationInMinutes * 60);

    let cancelled = false;
    async function fetchImages() {
      setLoadingQuestions(true);
      try {
        const folderRef = storageRef(storage, STORAGE_FOLDER + "/");
        const res = await listAll(folderRef);

        const items = await Promise.all(
          res.items.map(async (itemRef) => {
            const name = itemRef.name;
            const match = name.match(/(\d+)/);
            const id = match ? parseInt(match[1], 10) : null;
            let url = null;
            try {
              url = await getDownloadURL(storageRef(storage, `${STORAGE_FOLDER}/${name}`));
            } catch (err) {
              console.warn("Failed to get download URL for", name, err);
            }
            // Use the hard-coded answerKey to build the question
            const ak = id && answerKey[id] !== undefined ? String(answerKey[id]).trim() : null;
            const isMcq = ak && ["1", "2", "3", "4"].includes(ak);
            return {
              id: id,
              question: "",
              img: url,
              type: isMcq ? "mcq" : "numerical",
              correctAnswer: ak || "",
            };
          })
        );

        const valid = items.filter((it) => it && it.id !== null);
        valid.sort((a, b) => a.id - b.id);

        if (!cancelled) {
          setQuestions(valid);
        }
      } catch (err) {
        console.error("Error listing storage folder:", err);
        if (!cancelled) setQuestions([]);
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    }

    fetchImages();
    return () => {
      cancelled = true;
    };
  }, []); // This useEffect only runs once

  // Preload all images into browser cache
  useEffect(() => {
    if (questions.length === 0) return;
    questions.forEach((q) => {
      if (q.img) { const img = new Image(); img.src = q.img; }
    });
  }, [questions]);

  useEffect(() => {
    if (showTest && !showResult) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showTest, showResult]);

  const formatTime = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  // --- Image cache check ---
  const isImageCached = (url) => {
    const img = new Image();
    img.src = url;
    return img.complete;
  };

  // --- Fullscreen helpers ---
  const requestTestFullscreen = () => {
    fullscreenActive.current = true;
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
    if (req) req.call(el).catch(() => {});
  };
  const exitTestFullscreen = () => {
    fullscreenActive.current = false;
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
    if (exit && (document.fullscreenElement || document.webkitFullscreenElement)) exit.call(document).catch(() => {});
  };
  useEffect(() => {
    const onFSChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && fullscreenActive.current) {
        setShowFSPrompt(true);
      }
    };
    document.addEventListener('fullscreenchange', onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFSChange);
      document.removeEventListener('webkitfullscreenchange', onFSChange);
    };
  }, []);

  // --- Image zoom ---
  const handleImageClick = (src) => { setZoomedImage(src); document.body.style.overflow = 'hidden'; };
  const handleCloseZoom = () => { setZoomedImage(null); document.body.style.overflow = 'auto'; };

  // --- Navigation with spinner ---
  const goToQuestion = (idx) => {
    const q = questions[idx];
    if (q && q.img && !isImageCached(q.img)) setQuestionLoading(true);
    setCurrentQues(idx);
  };

  const handleStartTestClick = () => {
    if (currentUser) {
      setShowInstructions(true);
    } else {
      setShowLoginPopup(true);
    }
  };
  
  const handleLoginClick = async () => {
    setShowLoginPopup(false);
    try {
      await signInWithGoogle();
      setShowInstructions(true);
    } catch (error) {
      const cancelled = error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request';
      if (!cancelled) console.error("Google Sign-In failed:", error);
    }
  };

  const handleProceedToTest = () => {
    if (agreeChecked) {
      requestTestFullscreen();
      setShowInstructions(false);
      setShowTest(true);
    }
  };

  const handleAnswerChange = (value) => {
    if (!questions || !questions[currentQues]) return;
    setAnswers({ ...answers, [questions[currentQues].id]: value });
  };

  const handleSaveNext = () => {
    if (currentQues < questions.length - 1) goToQuestion(currentQues + 1);
  };

  const handleClear = () => {
    if (!questions || !questions[currentQues]) return;
    const newAnswers = { ...answers };
    delete newAnswers[questions[currentQues].id];
    setAnswers(newAnswers);
  };

  const handleMarkReview = () => {
    if (!questions || !questions[currentQues]) return;
    if (!marked.includes(questions[currentQues].id))
      setMarked([...marked, questions[currentQues].id]);
    handleSaveNext();
  };

  const handleMarkReviewOnly = () => {
    if (!questions || !questions[currentQues]) return;
    const newAnswers = { ...answers };
    delete newAnswers[questions[currentQues].id];
    setAnswers(newAnswers);
    if (!marked.includes(questions[currentQues].id))
      setMarked([...marked, questions[currentQues].id]);
    handleSaveNext();
  };

  const handleBack = () => {
    if (currentQues > 0) goToQuestion(currentQues - 1);
  };

  const handleNext = () => {
    if (currentQues < questions.length - 1) goToQuestion(currentQues + 1);
  };

  const handleSubmit = async () => {
    if (submissionPending.current) return;
    if (!currentUser || questions.length === 0) {
      setSubmissionError("Please sign in and load the test questions before submitting.");
      return;
    }
    submissionPending.current = true;
    setSubmitting(true);
    setSubmissionError("");
  
    const totalQuestions = questions.length;
    let attempted = 0, correct = 0, incorrect = 0, totalMarks = 0, negativeMarks = 0;
  
    const questionDetails = questions.map(q => {
      const userAnswer = answers[q.id] || null; 
      const isCorrect = userAnswer !== null && String(userAnswer).trim() === String(q.correctAnswer).trim();
  
      if (userAnswer !== null) {
        attempted++;
        if (isCorrect) {
          correct++;
          totalMarks += 4;
        } else {
          incorrect++;
          negativeMarks += 1;
        }
      }
  
      return {
        questionId: q.id,
        userAnswer: userAnswer,
        correctAnswer: q.correctAnswer,
        status: userAnswer === null ? 'unattempted' : isCorrect ? 'correct' : 'incorrect',
      };
    });
  
    const finalMarks = totalMarks - negativeMarks;
    try {
      const userRef = doc(db, "users", currentUser.uid);
      await runTransaction(db, async transaction => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User profile is unavailable.");
        const userData = userDoc.data();
        const testsTaken = userData.testsTaken || [];
        const previousAttempts = testsTaken.filter(t => t.testName === testName).length;
        const newAttempt = previousAttempts + 1;

        const resultData = {
          testName: testName,
          score: finalMarks,
          total: totalQuestions * 4,
          attempted: attempted,
          correct: correct,
          incorrect: incorrect,
          totalQuestions: totalQuestions,
          date: new Date().toISOString(),
          attempt: newAttempt,
          storagePath: STORAGE_FOLDER,
          questionDetails: questionDetails,
        };

        transaction.update(userRef, { testsTaken: [...testsTaken, resultData] });
        const subjectKeys = new Set((userData.enrolledPrograms || []).flatMap(id => PROGRAM_SUBJECT_KEYS[id] || []));
        subjectKeys.forEach(subjectKey => {
          (SUBJECT_CHAPTERS[subjectKey] || []).forEach(chapter => {
            if (!chapter.tests?.some(test => test.testId === "jee2020_1_1")) return;
            transaction.set(doc(db, "users", currentUser.uid, "progress", subjectKey), {
              [chapter.id]: {
                tests: { jee2020_1_1: { attempted: true, completed: true, completedAt: Date.now() } },
              },
            }, { merge: true });
          });
        });
      });
      exitTestFullscreen();
      setShowResult(true);
      setShowTest(false);
    } catch (err) {
      console.error("Error submitting test:", err);
      setSubmissionError("Your result could not be saved. Please check your connection and submit again. Your answers are still here.");
    } finally {
      submissionPending.current = false;
      setSubmitting(false);
    }
  };

  if (loadingQuestions) {
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>Loading test...</h2>
          <p>Fetching questions from server. Please wait.</p>
        </div>
      </div>
    );
  }

  if (showLoginPopup) {
    return (
      <div className="modal-backdrop" style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 1000
      }}>
        <div className="login-modal" style={{
          backgroundColor: '#fff', padding: '30px', borderRadius: '8px',
          textAlign: 'center', maxWidth: '400px', width: '90%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <h3 style={{ color: '#ef4444', marginBottom: '20px' }}>Login Required</h3>
          <p style={{ marginBottom: '30px' }}>Please log in with Google to start the test and save your progress.</p>
          <div className="button-row">
            <button onClick={handleLoginClick} className="start-test-btn"
              style={{ backgroundColor: '#4285F4', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>
              🔑 Login with Google
            </button>
            <button onClick={() => setShowLoginPopup(false)} className="back-btn"
              style={{ backgroundColor: '#6b7280', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!showInstructions && !showTest && !showResult) {
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>{testName}</h2>
          <p><strong>Subject:</strong> Physics, Chemistry, Mathematics</p>
          <p><strong>Duration:</strong> {durationInMinutes} Minutes</p>
          <p><strong>Total Questions:</strong> {numberQuestions}</p>
          <p><strong>Type:</strong> Single Choice Questions &amp; Numerical Questions</p>
          <p><strong>Marking:</strong> +4 for correct, &minus;1 for incorrect, 0 for unattempted.</p>
          <div className="button-row">
            <button className="start-test-btn" onClick={handleStartTestClick}>Start Test</button>
            <button className="back-btn" onClick={() => navigate(-1)}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  if (showInstructions) {
    return (
      <div className="instructions-container">
        <h2>Please read the instructions carefully</h2>
        <div className="instruction-box">
          <h3>General Instructions:</h3>
          <p>Total duration of {testName} is {durationInMinutes} min.</p>
          <p>The clock will be set at the server. The countdown timer in the top right corner of screen will display the remaining time available for you to complete the examination. When the timer reaches zero, the examination will end by itself.</p>
          <p>The Questions Palette displayed on the right side of screen will show the status of each question using one of the following symbols:</p>
          <ul>
            <li>🔵 You have not visited the question yet.</li>
            <li>⚪ You have not answered the question.</li>
            <li>🟢 You have answered the question.</li>
            <li>🟡 You have NOT answered the question, but have marked the question for review.</li>
            <li>🟣 The question(s) "Answered and Marked for Review" will be considered for evaluation.</li>
          </ul>
          <h4>Navigating to a Question:</h4>
          <ul>
            <li>Click the question number in the palette to go directly to it.</li>
            <li>Click Save &amp; Next to save your answer and move to the next question.</li>
            <li>Click Mark for Review &amp; Next to save your answer, mark for review, and go to the next question.</li>
          </ul>
          <h4>Answering a Question:</h4>
          <ul>
            <li>Click the option button to select your answer.</li>
            <li>Use Clear Response to deselect.</li>
            <li>Always click Save &amp; Next to save your answer.</li>
          </ul>
          <p className="declaration">
            I have read and understood the instructions. I declare that I am not in possession of any prohibited gadget or material.
          </p>
        </div>
        <div className="agree-section">
          <label>
            <input type="checkbox" checked={agreeChecked} onChange={(e) => setAgreeChecked(e.target.checked)} />{" "}
            I agree to the above instructions
          </label>
          <button className="proceed-btn" disabled={!agreeChecked} onClick={handleProceedToTest}>
            Start Test ➡
          </button>
        </div>
      </div>
    );
  }

  if (showTest) {
    const q = questions[currentQues];

    if (!q) {
      return (
        <div className="mock-container">
          <div className="mock-body"><div className="question-area"><h3>Loading questions...</h3></div></div>
        </div>
      );
    }

    return (
      <div className="mock-container">
        {/* Fullscreen return prompt */}
        {showFSPrompt && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
            <p style={{ color: '#fff', fontSize: '1.2rem', textAlign: 'center', margin: '0 20px' }}>⚠️ Fullscreen is required during the test.<br/>Please return to fullscreen to continue.</p>
            <button onClick={() => { requestTestFullscreen(); setShowFSPrompt(false); }} style={{ padding: '14px 36px', fontSize: '1rem', fontWeight: 600, background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>🔲 Return to Fullscreen</button>
          </div>
        )}
        {/* Image zoom modal */}
        {zoomedImage && (
          <div className="image-modal-overlay">
            <div className="image-modal-backdrop" onClick={handleCloseZoom} />
            <div className="image-modal-content">
              <button className="image-modal-close" onClick={handleCloseZoom}>✕</button>
              <img src={zoomedImage} alt="Question Full View" className="image-modal-img" />
            </div>
          </div>
        )}
        {/* Sticky header */}
        <div className="test-header-forced">
          <div className="header-left-info">
            <span className="user-emoji">👤</span>
            <div className="user-details">
              <div className="info-line"><span className="label-bold">Candidate:</span><span className="value-text">{userName || "Guest"}</span></div>
              <div className="info-line"><span className="label-bold">Test:</span><span className="value-text">{testName}</span></div>
            </div>
          </div>
          <div className="header-timer-section">
            <div className="timer-label">Remaining Time:</div>
            <div className="timer-big-value" style={{ color: timeLeft < 600 ? '#f44336' : '#fff' }}>{formatTime(timeLeft)}</div>
            <div className="timer-total">Duration: {durationInMinutes} min</div>
          </div>
        </div>

        <div className="mock-body">
          <div className="question-area">
            {questionLoading && (
              <div className="question-loading-overlay">
                <div className="question-spinner"></div>
                <p className="question-loading-text">Loading question...</p>
              </div>
            )}
            <div className="question-clickable" onClick={() => q.img && handleImageClick(q.img)} style={q.img ? { cursor: 'zoom-in' } : {}}>
              <h4>Question {q.id}</h4>
              {q.img && (
                <div className="image-section">
                  <div className="question-image-wrapper">
                    <img src={q.img} alt={`Q${q.id}`} onLoad={() => setQuestionLoading(false)} onError={() => setQuestionLoading(false)} />
                  </div>
                </div>
              )}
            </div>

            {q.type === "mcq" && (
              <div className="options">
                {[1, 2, 3, 4].map((n) => {
                  const val = String(n);
                  return (
                    <label key={n} className="option">
                      <input type="radio" name={`q${q.id}`} value={val} checked={answers[q.id] === val} onChange={(e) => handleAnswerChange(e.target.value)} />
                      {`${val})`}
                    </label>
                  );
                })}
              </div>
            )}
            {q.type === "numerical" && (
              <input type="text" className="numeric-input" value={answers[q.id] || ""} onChange={(e) => handleAnswerChange(e.target.value)} placeholder="Enter your answer" />
            )}

            <div className="button-row">
              <button className="save-btn" onClick={handleSaveNext}>Save &amp; Next</button>
              <button className="clear-btn" onClick={handleClear}>Clear Response</button>
              <button className="mark-only-btn" onClick={handleMarkReviewOnly}>Mark for Review</button>
              <button className="mark-btn" onClick={handleMarkReview}>Answered &amp; Mark</button>
            </div>
            <div className="button-row" style={{ marginTop: "15px" }}>
              <button className="nav-btn" onClick={handleBack}>◀ Back</button>
              <button className="nav-btn" onClick={handleNext}>Next ▶</button>
              <button className="submit-btn" disabled={submitting} onClick={handleSubmit}>{submitting ? "Submitting..." : "Submit Test"}</button>
              <button className="close-btn1" disabled={submitting} onClick={() => { exitTestFullscreen(); navigate(-1); }}>❌ Close Test</button>
            </div>
            {submissionError && <p role="alert">{submissionError}</p>}
          </div>

          <div className="palette-col">
            <div className="question-palette">
              <h4>Question Palette</h4>
              <div className="palette-grid">
                {questions.map((qq, i) => {
                  const isAnswered = answers[qq.id] !== undefined && answers[qq.id] !== null && answers[qq.id] !== "";
                  const isMarked = marked.includes(qq.id);
                  const isVisited = visited.includes(qq.id);
                  const isCurrent = currentQues === i;
                  let cls = "palette-btn";
                  if (isAnswered && isMarked) cls += " answered-marked";
                  else if (isAnswered) cls += " answered";
                  else if (isMarked) cls += " marked";
                  else if (!isVisited) cls += " not-visited";
                  if (isCurrent) cls += " active";
                  return <button key={qq.id} className={cls} onClick={() => goToQuestion(i)}>{i + 1}</button>;
                })}
              </div>
              <div className="palette-legend">
                <div className="legend-item"><span className="legend-box not-visited"></span><span>Not Visited</span></div>
                <div className="legend-item"><span className="legend-box" style={{ background: 'white', border: '2px solid #ccc', borderRadius: '3px', display: 'inline-block', width: '16px', height: '16px' }}></span><span>Not Answered</span></div>
                <div className="legend-item"><span className="legend-box answered"></span><span>Answered</span></div>
                <div className="legend-item"><span className="legend-box marked"></span><span>Marked (Not Eval.)</span></div>
                <div className="legend-item"><span className="legend-box answered-marked"></span><span>Answered &amp; Marked (Eval.)</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResult) {
    const totalQuestions = questions.length;
    let attempted = 0, correct = 0, incorrect = 0;

    questions.forEach((q) => {
      const ans = answers[q.id];
      if (ans && ans !== "") {
        attempted++;
        if (String(ans).trim() === String(q.correctAnswer).trim()) correct++;
        else incorrect++;
      }
    });

    const finalMarks = (correct * 4) - incorrect;

    return (
      <div className="mock-container">
        <div className="result-header">
          <h2>Test Result &amp; Analysis</h2>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button className="result-back-btn" onClick={() => navigate(-1)}>⬅️ Back to Lectures</button>
            <button className="result-back-btn" style={{ background: 'rgba(255,255,255,0.2)' }} onClick={() => navigate("/profile")}>View My Profile ➡️</button>
          </div>
        </div>
        <div className="mock-body" style={{ flexDirection: 'column' }}>
          <div className="question-area" style={{ flex: 1 }}>
            <div className="result-summary">
              <p className="result-candidate">👤 Candidate: {userName}</p>
              <div className="summary-cards">
                <div className="card attempted">Attempted: {attempted}</div>
                <div className="card correct">Correct: {correct}</div>
                <div className="card incorrect">Incorrect: {incorrect}</div>
                <div className="card total">Total Questions: {totalQuestions}</div>
                <div className="card marks">Score: {finalMarks} / {totalQuestions * 4}</div>
              </div>
            </div>
            <h4 className="detail-heading">Question-wise Details:</h4>
            <div className="question-details" style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr', gap: '15px', marginTop: '20px' }}>
              {questions.map((q, i) => (
                <div key={q.id} className={`question-detail ${answers[q.id] === q.correctAnswer ? 'correct' : answers[q.id] ? 'incorrect' : 'unattempted'}`}>
                  <p style={{ fontWeight: '700', marginBottom: 4 }}>Q. {i + 1}</p>
                  {q.img && <div style={{ marginBottom: 8 }}><img src={q.img} alt={`Q${i + 1}`} className="question-detail-img" /></div>}
                  <p>Your Answer: {answers[q.id] || "Not Answered"} | Correct: {q.correctAnswer || "—"}</p>
                  <p>Marks: {answers[q.id] === q.correctAnswer ? "+4" : answers[q.id] ? "-1" : "0"}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
